import { action, computed, observable } from 'mobx';

import { SerializedData } from 'bernie-core';
import { Store } from 'bernie-plugin-mobx';

import { JourneyType } from 'types/search/listings';
import {
  FlightsShoppingPwaFlightInformationResults,
  FlightsJourneyAvailableFares,
  FlightsSelectedJourneyAvailableFares
} from '__generated__/api';
import FareSummary = FlightsShoppingPwaFlightInformationResults.FareSummary;

export class DetailsAndFaresStore extends Store {
  @observable public availableFaresInformation: Array<
    FlightsJourneyAvailableFares.Fragment | FlightsSelectedJourneyAvailableFares.Fragment
  >;
  @observable public hygieneAmenitiesPresentation: FlightsJourneyAvailableFares.HygieneAmenitiesPresentation;
  @observable public detailsVisible: boolean;
  @observable private initialSelectedFareIndex: number;
  @observable public currentOfferSelectedjourneyContinuationId: string;
  @observable public isUpsellLoaded: boolean;
  @observable public availableFaresCount: number;
  @observable public selectFareActionSelectedText: string;
  @observable public selectFareActionSelectText: string;
  @observable public selectedIndex = -1;
  @observable public highlightNextFare = false;

  @observable private selectedOfferRef: React.Ref<HTMLButtonElement>;
  public appliedFareType: string;

  @computed get selectedFareType() {
    const fares = this.availableFaresInformation?.[0]?.fareChoiceInformation
      ?.fares as FlightsJourneyAvailableFares.Fares[];

    return fares && fares.filter(fareType => fareType.selected)[0];
  }

  public setSelectedOfferRef = (ref): void => {
    this.selectedOfferRef = ref;
  };

  public getSelectedOfferRef = () => {
    return this.selectedOfferRef;
  };

  @action
  public revertInitialSelectedFareBack = () => {
    const availableFaresInformation = this.availableFaresInformation[0];
    const fares = availableFaresInformation?.fareChoiceInformation?.fares as FlightsJourneyAvailableFares.Fares[];

    if (!fares || fares?.[this.initialSelectedFareIndex]?.selected) {
      return;
    }

    fares.map((fare, fareIndex) => {
      if (fare.selected) {
        fare.selected = false;
      }

      if (fareIndex === this.initialSelectedFareIndex) {
        fare.selected = true;
      }
    });

    this.cleanInitialSelectedFareIndex();
  };

  public cleanInitialSelectedFareIndex = () => {
    this.initialSelectedFareIndex = -1;
  };

  public cleanSelectedState = () => {
    this.detailsVisible = false;
    this.selectedIndex = -1;
  };

  public saveInitialSelectedFare = () => {
    const availableFaresInformation = this.availableFaresInformation[0];
    const fares = availableFaresInformation?.fareChoiceInformation?.fares as FlightsJourneyAvailableFares.Fares[];

    if (fares) {
      this.initialSelectedFareIndex = fares.findIndex(fareType => fareType.selected);
    }
  };

  @action
  public selectFareType = (selectedFareIndex: number) => {
    const availableFaresInformation = this.availableFaresInformation[0];
    const fares = availableFaresInformation?.fareChoiceInformation?.fares as FlightsJourneyAvailableFares.Fares[];

    fares.map((fare, fareIndex) => {
      if (fare.selected) {
        fare.selected = false;
      }

      if (fareIndex === selectedFareIndex) {
        fare.selected = true;
      }
    });
  };

  public getAvailableFaresInformation = (journeyIndex?: number) => {
    if (journeyIndex >= 0 && this.availableFaresInformation?.[journeyIndex]) {
      return this.availableFaresInformation[journeyIndex];
    }

    return this.availableFaresInformation;
  };

  public getAppliedFareType = () => {
    const availableFaresInformation = this.availableFaresInformation[0];
    const fares = availableFaresInformation?.fareChoiceInformation?.fares as FlightsJourneyAvailableFares.Fares[];

    return fares.find(fare => {
      return fare.identifier === this.appliedFareType;
    });
  };

  public setIsUpsellLoaded = (isUpsellLoaded: boolean) => {
    this.isUpsellLoaded = isUpsellLoaded;
  };

  public setAvailableFaresCount = (availableFaresCount: number) => {
    if (availableFaresCount) {
      this.availableFaresCount = availableFaresCount;
    }
  };
  public setDetailsVisibility = () => {
    if (this.detailsVisible) {
      this.detailsVisible = false;
      setTimeout(() => {
        this.detailsVisible = true;
      }, 0);
    } else {
      this.detailsVisible = true;
    }
  };

  public setAvailableFaresInformationFromJourney = (journeys: JourneyType[]) => {
    this.availableFaresInformation = journeys.map(journey => journey.flightsJourneyAvailableFaresInformation);
  };

  public setCurrentOfferSelectedjourneyContinuationId = (currentOfferSelectedjourneyContinuationId: string) => {
    this.currentOfferSelectedjourneyContinuationId = currentOfferSelectedjourneyContinuationId;
  };

  public setAvailableFaresInformationFromUpsellSlicing = (journeys: JourneyType[]) => {
    this.availableFaresInformation = journeys.map(journey => journey.flightsJourneyAvailableFaresInformation);
  };

  public setAvailableFaresInformationFromFareSummary = (fareSummary: FareSummary) => {
    this.availableFaresInformation = [fareSummary.flightsSelectedJourneyAvailableFaresInformation];
  };

  public setAppliedFareType = fareType => {
    this.appliedFareType = fareType;
  };

  public setHygieneAmenitiesPresentation = (journeys: JourneyType[]) => {
    this.hygieneAmenitiesPresentation =
      journeys?.[0]?.flightsJourneyAvailableFaresInformation?.hygieneAmenitiesPresentation;
  };

  public setHighlightNextFare = (value: boolean): void => {
    this.highlightNextFare = value;
  };

  public shouldHighlightNextFare = () => {
    return this.highlightNextFare;
  };

  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
  }
}

DetailsAndFaresStore.DEFAULTS = {
  detailsVisible: false,
  isUpsellLoaded: true,
  availableFaresCount: 0,
  availableFaresInformation: {},
  appliedFareType: null,
  initialSelectedFareIndex: -1,
  selectedIndex: -1,
  selectFareActionSelectedText: null,
  selectFareActionSelectText: null
};
